import React, { useEffect, useState } from 'react';
import { Button, Input } from '../../components/StyledComponents';
import { Section } from '../../components/ui/Section';
import Store from '../../store/Store';
import { IconClose } from '../../components/Icons';
import { useDialog } from '../../hooks/useDialog';

interface DroneTypeInfo {
	id: number;
	name: string;
	reachDiameter: number;
	flightSpeed: number;
	startStopTime: number;
	maxFlightTime: number;
	fhModelKey?: string;
	fhDomain?: string;
	fhType?: string;
	fhSubType?: string;
	fhDeviceClass?: string;
}

interface DroneInfo {
	id: number;
	name: string;
	serialNumber?: string;
	callsign?: string;
	deviceOnlineStatus?: boolean;
	modeCode?: number;
	droneType: DroneTypeInfo;
}

interface FlightHubDroneModelInfo {
	key: string;
	domain: string;
	type: string;
	subType: string;
	name: string;
	deviceClass: string;
}

interface AvailableDroneSn {
	sn: string;
	callsign: string;
	online: boolean;
	modeCode?: number;
	deviceModel?: FlightHubDroneModelInfo;
}

type FormMode = 'idle' | 'create' | 'edit';

const emptyPerf = { reachDiameter: '3000', flightSpeed: '900', startStopTime: '1.5', maxFlightTime: '27' };

export default function DronePage() {
	const { showConfirmDialog } = useDialog();
	const [drones, availableDroneSns] = Store.use('drones', 'availableDroneSns') as [
		DroneInfo[] | undefined,
		AvailableDroneSn[] | undefined,
	];

	const [mode, setMode] = useState<FormMode>('idle');
	const [editId, setEditId] = useState<number | null>(null);
	const [name, setName] = useState('');
	const [selectedSn, setSelectedSn] = useState('');
	const [perf, setPerf] = useState(emptyPerf);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		Store.loadDrones();
		Store.loadAvailableDroneSns();
	}, []);

	const selectedSnInfo = availableDroneSns?.find((s) => s.sn === selectedSn);

	const openCreate = () => {
		setMode('create');
		setEditId(null);
		setName('');
		setSelectedSn('');
		setPerf(emptyPerf);
	};

	const openEdit = (drone: DroneInfo) => {
		setMode('edit');
		setEditId(drone.id);
		setName(drone.name);
		setSelectedSn(drone.serialNumber ?? '');
		setPerf({
			reachDiameter: String(drone.droneType.reachDiameter),
			flightSpeed: String(drone.droneType.flightSpeed),
			startStopTime: String(drone.droneType.startStopTime),
			maxFlightTime: String(drone.droneType.maxFlightTime),
		});
	};

	const cancel = () => {
		setMode('idle');
		setEditId(null);
	};

	const perfValid =
		perf.reachDiameter !== '' &&
		perf.flightSpeed !== '' &&
		perf.startStopTime !== '' &&
		perf.maxFlightTime !== '';

	const canSave =
		name.trim().length > 0 &&
		perfValid &&
		(mode === 'edit' || selectedSn.length > 0);

	const handleSave = async () => {
		setSaving(true);
		try {
			if (mode === 'create') {
				await Store.createDrone(
					name.trim(),
					selectedSn,
					Number(perf.reachDiameter),
					Number(perf.flightSpeed),
					Number(perf.startStopTime),
					Number(perf.maxFlightTime)
				);
				Store.setPopupMessage('ok', 'Drohne erstellt', `"${name}" wurde erfolgreich erstellt.`);
			} else if (mode === 'edit' && editId !== null) {
				await Store.updateDrone(
					editId,
					name.trim(),
					Number(perf.reachDiameter),
					Number(perf.flightSpeed),
					Number(perf.startStopTime),
					Number(perf.maxFlightTime)
				);
				Store.setPopupMessage('ok', 'Drohne aktualisiert', `"${name}" wurde erfolgreich gespeichert.`);
			}
			setMode('idle');
			setEditId(null);
		} catch {
			// errors shown by Store
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = (drone: DroneInfo) => {
		showConfirmDialog({
			title: 'Drohne löschen',
			text: `Sind Sie sicher, dass Sie "${drone.name}" löschen möchten?`,
			confirmLabel: 'Ja',
			cancelLabel: 'Nein',
			confirmFirst: true,
			onConfirm: () => {
				Store.deleteDrone(drone.id);
			},
		});
	};

	return (
		<div className="order-page-left">
			{/* Drone list */}
			<Section>
				<div className="flex items-center justify-between mb-2">
					<h2 className="m-0">Drohnen Verwalten</h2>
					<button
						className="text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
						onClick={openCreate}
					>
						Neue Drohne Anlegen
					</button>
				</div>

				{drones && drones.length > 0 ? (
					<div className="flex flex-col gap-2">
						{drones.map((drone) => (
							<div
								key={drone.id}
								className={`rounded-lg border p-3 flex flex-col gap-1.5 text-sm cursor-pointer transition-colors ${
									editId === drone.id
										? 'border-blue-400 bg-blue-50'
										: 'border-gray-200 bg-gray-50 hover:border-gray-300'
								}`}
								onClick={() => openEdit(drone)}
							>
								<div className="flex items-center justify-between gap-2">
									<span className="font-semibold text-gray-800">{drone.name}</span>
									<div className="flex items-center gap-2">
										{drone.deviceOnlineStatus !== undefined && (
											<span
												className={`text-xs px-2 py-0.5 rounded-full font-medium ${
													drone.deviceOnlineStatus
														? 'bg-green-100 text-green-700'
														: 'bg-gray-200 text-gray-500'
												}`}
											>
												{drone.deviceOnlineStatus ? 'Online' : 'Offline'}
											</span>
										)}
										<div
											role="button"
											className="text-gray-400 hover:text-red-500 transition-colors"
											onClick={(e) => {
												e.stopPropagation();
												handleDelete(drone);
											}}
										>
											<IconClose />
										</div>
									</div>
								</div>

								{drone.callsign && (
									<div className="flex gap-2">
										<span className="text-gray-500 w-28 shrink-0">Callsign</span>
										<span className="text-gray-800">{drone.callsign}</span>
									</div>
								)}
								{drone.serialNumber && (
									<div className="flex gap-2">
										<span className="text-gray-500 w-28 shrink-0">Serial</span>
										<span className="text-gray-800 font-mono text-xs">{drone.serialNumber}</span>
									</div>
								)}

								<div className="flex flex-col gap-1 pt-1 border-t border-gray-200 mt-0.5">
									<span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
										Modell: {drone.droneType.name}
									</span>
									<div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600">
										<span>Reichweite: {drone.droneType.reachDiameter} m</span>
										<span>Geschwindigkeit: {drone.droneType.flightSpeed} m/s</span>
										<span>Max Flugzeit: {drone.droneType.maxFlightTime} min</span>
										<span>Start/Stop: {drone.droneType.startStopTime} s</span>
									</div>
									{drone.droneType.fhDeviceClass && (
										<div className="flex gap-3 text-xs text-gray-400 mt-0.5">
											<span>Class: {drone.droneType.fhDeviceClass}</span>
											{drone.droneType.fhModelKey && <span>Key: {drone.droneType.fhModelKey}</span>}
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="text-sm text-gray-500">Keine Drohnen vorhanden.</div>
				)}
			</Section>

			{/* Create / Edit form */}
			{mode !== 'idle' && (
				<Section>
					<h2>{mode === 'create' ? 'Neue Drohne erstellen' : 'Drohne bearbeiten'}</h2>

					<div className="flex flex-col gap-3">
						<Input
							label="Name"
							value={name}
							onChange={(v) => setName(String(v))}
						/>

						{mode === 'create' && (
							<div className="flex flex-col gap-1.5">
								<label className="text-sm font-medium text-gray-700">FlightHub Drohnen-SN *</label>
								{availableDroneSns && availableDroneSns.length > 0 ? (
									<select
										className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
										value={selectedSn}
										onChange={(e) => setSelectedSn(e.target.value)}
									>
										<option value="">SN auswählen...</option>
										{availableDroneSns.map((d) => (
											<option key={d.sn} value={d.sn}>
												{d.callsign} ({d.sn}){d.online ? ' ✓' : ''}
											</option>
										))}
									</select>
								) : (
									<div className="text-sm text-gray-500 italic">
										Keine verfügbaren FlightHub-Drohnen gefunden.
									</div>
								)}

								{selectedSnInfo && (
									<div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1.5 text-sm">
										<div className="flex items-center gap-2">
											<span className="font-medium text-gray-800">{selectedSnInfo.callsign}</span>
											<span
												className={`text-xs px-2 py-0.5 rounded-full font-medium ${
													selectedSnInfo.online
														? 'bg-green-100 text-green-700'
														: 'bg-gray-200 text-gray-500'
												}`}
											>
												{selectedSnInfo.online ? 'Online' : 'Offline'}
											</span>
										</div>
										<div className="flex gap-2">
											<span className="text-gray-500 w-28 shrink-0">Serial</span>
											<span className="font-mono text-xs text-gray-800">{selectedSnInfo.sn}</span>
										</div>
										{selectedSnInfo.modeCode !== undefined && (
											<div className="flex gap-2">
												<span className="text-gray-500 w-28 shrink-0">Mode</span>
												<span className="text-gray-800">{selectedSnInfo.modeCode}</span>
											</div>
										)}
										{selectedSnInfo.deviceModel && (
											<div className="flex flex-col gap-1 pt-1 border-t border-gray-200">
												<span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
													Gerätemodell
												</span>
												<div className="flex gap-2">
													<span className="text-gray-500 w-28 shrink-0">Name</span>
													<span className="text-gray-800">{selectedSnInfo.deviceModel.name}</span>
												</div>
												<div className="flex gap-2">
													<span className="text-gray-500 w-28 shrink-0">Klasse</span>
													<span className="text-gray-800">{selectedSnInfo.deviceModel.deviceClass}</span>
												</div>
												<div className="flex gap-3 text-xs text-gray-400">
													<span>Domain: {selectedSnInfo.deviceModel.domain}</span>
													<span>Type: {selectedSnInfo.deviceModel.type}</span>
													<span>Sub: {selectedSnInfo.deviceModel.subType}</span>
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						)}

						<div className="pt-1 border-t border-gray-200">
							<p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
								Leistungsparameter
							</p>
							<div className="grid grid-cols-2 gap-3">
								<Input
									label="Reichweite (m)"
									type="number"
									value={String(perf.reachDiameter)}
									onChange={(v) => setPerf((p) => ({ ...p, reachDiameter: String(v) }))}
								/>
								<Input
									label="Geschwindigkeit (m/s)"
									type="number"
									value={String(perf.flightSpeed)}
									onChange={(v) => setPerf((p) => ({ ...p, flightSpeed: String(v) }))}
								/>
								<Input
									label="Max Flugzeit (min)"
									type="number"
									value={String(perf.maxFlightTime)}
									onChange={(v) => setPerf((p) => ({ ...p, maxFlightTime: String(v) }))}
								/>
								<Input
									label="Start/Stop Zeit (s)"
									type="number"
									value={String(perf.startStopTime)}
									onChange={(v) => setPerf((p) => ({ ...p, startStopTime: String(v) }))}
								/>
							</div>
						</div>

						<div className="flex gap-2 pt-1">
							<Button enabled={canSave && !saving} onClick={handleSave}>
								{saving ? 'Speichern...' : mode === 'create' ? 'Erstellen' : 'Speichern'}
							</Button>
							<button
								className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
								onClick={cancel}
							>
								Abbrechen
							</button>
						</div>
					</div>
				</Section>
			)}
		</div>
	);
}
